/**
 * Azure agent / ARM connection helpers.
 *
 * Pure helpers for deriving Azure AI (agent-service / Foundry) connection
 * identifiers from ARM resource IDs, host names, and node parameter values.
 * Extracted from `ConnectionCreationPanel`, `ConnectionPicker`, and
 * `connectionStore` so every Azure-agent connection surface shares one
 * implementation instead of maintaining parallel copies.
 */

/**
 * Extract the Azure CognitiveServices account name from an ARM resource ID.
 * Works for both account IDs (`.../accounts/{name}`) and project IDs
 * (`.../accounts/{name}/projects/{proj}`).
 * Returns the account name, or `undefined` if the pattern isn't found.
 */
export function extractArmAccountName(armId: string): string | undefined {
  const match = armId.match(/\/accounts\/([^/]+)/i);
  return match?.[1];
}

/**
 * Normalize a CognitiveServices ARM resource ID down to the parent account,
 * stripping a trailing `/projects/{proj}` segment when present.
 *
 * Foundry connections store the project-scoped ARM ID
 * (`.../accounts/{account}/projects/{project}`) because the backend's
 * `FoundryProjectResourceReference` requires that full path. ARM data-plane
 * calls that operate on the account (e.g. listing model deployments) only
 * support the `accounts/{account}` resource type, not `accounts/projects` —
 * calling them with the project-scoped ID returns an ARM error. Account-only
 * IDs (no `/projects/` suffix) are returned unchanged.
 */
export function toAccountArmResourceId(armId: string): string {
  return armId.replace(/\/projects\/[^/]+\/?$/i, '');
}

/**
 * Normalize an Azure agent (Foundry) host value into a canonical URL string.
 * Accepts a full URL (returned normalized via `URL`) or a bare short resource
 * name, which is expanded to `https://{name}.services.ai.azure.com` for
 * back-compat with previously-saved short names. Non-string or empty input
 * yields `undefined`.
 */
export function normalizeAzureAgentHostUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  try {
    return new URL(trimmed).toString();
  } catch {
    // Back-compat for previously saved short names, e.g. "my-foundry-resource".
    return `https://${trimmed}.services.ai.azure.com`;
  }
}

/**
 * Derive `connectionProperties` overrides for managed API connections that need
 * values not present in the OAuth form (e.g. `azureAgentAIResourceName` for
 * `azureagentservice`). Looks for the Foundry endpoint in common parameter keys
 * first, then falls back to the node's own inputs. Returns `undefined` for
 * non-`azureagentservice` connectors or when no usable endpoint can be found.
 */
export function deriveAzureAgentServiceConnectionOverrides(
  resolvedConnectorName: string | undefined,
  parameterValues: Record<string, unknown>,
  node: { nodeData?: unknown } | null | undefined,
): Record<string, unknown> | undefined {
  if (!resolvedConnectorName || !resolvedConnectorName.toLowerCase().includes('azureagentservice')) return undefined;
  // Try common parameter keys where the Foundry endpoint is stored
  const endpointKeys = ['endpoint', 'parameters.endpoint', 'inputs.parameters.endpoint'];
  let endpoint: string | undefined;
  for (const key of endpointKeys) {
    const v = parameterValues[key];
    if (typeof v === 'string' && v.includes('.')) { endpoint = v; break; }
  }
  // Also check node inputs directly
  if (!endpoint) {
    const inputs = (node?.nodeData as Record<string, unknown>)?.inputs as Record<string, unknown> | undefined;
    const params = inputs?.parameters as Record<string, unknown> | undefined;
    const ep = params?.endpoint ?? inputs?.endpoint;
    if (typeof ep === 'string') endpoint = ep;
  }
  if (!endpoint) return undefined;
  try {
    const normalizedEndpoint = new URL(endpoint).toString();
    return normalizedEndpoint ? { azureAgentAIResourceName: normalizedEndpoint } : undefined;
  } catch { return undefined; }
}
